import { trackSignUpStart } from '@/helpers/telemetry/trackSignUpStart'

import {
  Container,
  SubHeader,
  TextBold,
  TextItalic,
  StyledParagraph,
  StyledInnerParagraph,
  StyledLink,
  List,
  SubList,
  ListItem,
} from './ui'

const CostcoAboutDetails = () => {
  return (
    <Container>
      <StyledParagraph>
        The Costco Wholesale marketplace is a B2B marketplace intended for those
        businesses involved in wholesale and/or resale selling their merchandise
        in-store, online through a company website, and/or via third-party
        seller platforms.
      </StyledParagraph>
      <StyledParagraph>
        Costco Wholesale Liquidations is the official B2B liquidation
        marketplace of the nation&apos;s top membership-only warehouse club,
        Costco Wholesale. Selling palletized apparel, automotive, major
        appliances, computers & tablets, consumer electronics, cookware,
        domestics, food & sundries, footwear, garden & patio, handbags,
        hardware, health & beauty, home furnishings, jewelry & watches, luggage,
        mixed lots, rugs, seasonal, toys, sporting goods and more.
      </StyledParagraph>
      <StyledParagraph>
        Apply to{' '}
        <StyledLink href="https://bstock.com/buy/seller/costco">
          Costco Wholesale Liquidations
        </StyledLink>{' '}
        in order to buy Costco liquidation inventory. Please take a moment to
        review the <TextBold>Marketplace Buyer Restrictions</TextBold> (
        <StyledLink href="#marketplace-buyer-requirements">
          <TextItalic>below</TextItalic>
        </StyledLink>
        ) to understand the buyer approval process for Costco Wholesale
        Liquidations. Only approved buyers can view Costco auctions.
      </StyledParagraph>
      <SubHeader>Product Categories</SubHeader>
      <StyledParagraph>
        Costco Wholesale Liquidations offers a wide range of categories of
        liquidation merchandise. Here&apos;s a list of typical inventory
        categories sold on Costco Wholesale Liquidations:
      </StyledParagraph>
      <List>
        <ListItem>
          Apparel
          <SubList>
            <ListItem>Men&apos;s Apparel</ListItem>
            <ListItem>Women&apos;s Apparel</ListItem>
            <ListItem>Girls Apparel</ListItem>
            <ListItem>Boys Apparel</ListItem>
          </SubList>
        </ListItem>
        <ListItem>Computers & Tablets</ListItem>
        <ListItem>Consumer Electronics</ListItem>
        <ListItem>Cookware</ListItem>
        <ListItem>Domestics</ListItem>
        <ListItem>Food & Sundries</ListItem>
        <ListItem>Footwear</ListItem>
        <ListItem>Garden / Patio</ListItem>
        <ListItem>Handbags</ListItem>
        <ListItem>Hardware</ListItem>
        <ListItem>Health & Beauty</ListItem>
        <ListItem>Home Furnishings</ListItem>
        <ListItem>Luggage</ListItem>
        <ListItem>Major Appliances</ListItem>
        <ListItem>Mixed Lots</ListItem>
        <ListItem>Rugs</ListItem>
        <ListItem>Small Appliances</ListItem>
        <ListItem>Sporting Goods</ListItem>
        <ListItem>Toys</ListItem>
      </List>
      <SubHeader>Types of Liquidated Inventory</SubHeader>
      <StyledParagraph>
        The selection, quality, and quantity of available products liquidated
        through Costco Wholesale Liquidations depend on a number of factors
        including: returns, shelf pulls and excess inventory.
      </StyledParagraph>
      <List>
        <ListItem>
          <TextBold>Member Returns</TextBold>
          <StyledInnerParagraph>
            Inventory that was purchased by customers and returned to the store
            for any of a number of reasons. The quality of inventory is
            dependent on the amount of use by the customer before returning and
            whether it was damaged or worn out in the process. Typically, Member
            Returns product shows little to no damage.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Excess Inventory</TextBold>
          <StyledInnerParagraph>
            Inventory that did not sell and is in too small a quantity to place
            on typical Costco displays.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Rare Shelf Pulls</TextBold>
          <StyledInnerParagraph>
            Inventory that is not in sellable condition directly to customers.
            The merchandise may or may not be functional with missing or broken
            parts.
          </StyledInnerParagraph>
        </ListItem>
      </List>
      <SubHeader>Inventory Conditions</SubHeader>
      <StyledParagraph>
        <TextBold>General Merchandise</TextBold>
      </StyledParagraph>
      <List>
        <ListItem>
          <TextBold>New:</TextBold> Unsold merchandise with no signs of use.
        </ListItem>
        <ListItem>
          <TextBold>Like New:</TextBold> Unused merchandise with minimal signs
          of handling.
        </ListItem>
        <ListItem>
          <TextBold>Refurbished:</TextBold> Merchandise that has been repaired
          to full functionality but may have signs of wear or use unless
          otherwise specified.
        </ListItem>
        <ListItem>
          <TextBold>Used &ndash; Good:</TextBold> Merchandise with signs of use
          from handling and/or customer exposure that is typically sellable
          without repairs needed.
        </ListItem>
        <ListItem>
          <TextBold>Used &ndash; Fair:</TextBold> Merchandise with heavy signs
          of use from handling and/or customer exposure that is likely in need
          of repair prior to resale.
        </ListItem>
        <ListItem>
          <TextBold>Salvage:</TextBold> Merchandise requiring significant
          repairs prior to resale or else will be used for parts/components
          only.
        </ListItem>
        <ListItem>
          <TextBold>Mixed:</TextBold> Can include products of any quality,
          including salvage. There&apos;s no guarantee of one particular product
          condition unless specified in the manifest or auction details.
        </ListItem>
      </List>
      <StyledParagraph>
        <TextBold>Appliances</TextBold>
      </StyledParagraph>
      <List>
        <ListItem>
          <TextBold>Scratch & Dent:</TextBold> Unused merchandise with signs of
          handling and/or warehouse exposure that is sellable without repairs
          needed.
        </ListItem>
        <ListItem>
          <TextBold>Used:</TextBold> Merchandise with visible signs of use
          and/or functional damage that may include but is not limited to
          water/food stains, lint/detergent present, scratches/dents, missing
          parts, etc.
        </ListItem>
      </List>
      <StyledParagraph>
        <TextBold>Consumer Electronics</TextBold>
      </StyledParagraph>
      <List>
        <ListItem>
          <TextBold>Data Clear Failed - Apple</TextBold>
          <StyledInnerParagraph>
            Data Clear Failed Units - Note that this listing is for bid solely
            by R2-certified buyers, who certify safeguarding of any potential
            customer data which may remain on these hard drives. The majority of
            these failed units were determined to have bad hard drives but they
            were not officially tested in any manner to confirm this or any
            other potential reason the computers would be classified as failed
            units. All products are member returns and sold AS IS with no return
            privileges. Cosmetic condition ranges from Like New (Open Box) to
            Damaged. The vast majority of units have been returned by customer
            within less than 90 days of original purchase. 40% of units in use
            for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Data Clear Failed - Mixed</TextBold>
          <StyledInnerParagraph>
            Note that this listing is for bid solely by R2-certified buyers, who
            certify safeguarding of any potential customer data which may remain
            on these hard drives. The majority of these failed units were
            determined to have bad hard drives but they were not officially
            tested in any manner to confirm this or any other potential reason
            the computers would be classified as failed units. All products are
            member returns and sold AS IS with no return privileges. Cosmetic
            condition ranges from Like New (Open Box) to Damaged. The vast
            majority of units have been returned by customer within less than 90
            days of original purchase. 30% of units in use for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Data Cleared - Apple</TextBold>
          <StyledInnerParagraph>
            All products are member returns and sold AS IS with no return
            privileges. Cosmetic condition ranges from Like New (Open Box) to
            Damaged. All units have been data-cleared by an R2-certified
            facility, however not all features have been tested. The vast
            majority of units have been returned by customer within less than 90
            days of original purchase. 40% of units in use for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Data Cleared - Mixed</TextBold>
          <StyledInnerParagraph>
            All products are member returns and sold AS IS with no return
            privileges. Cosmetic condition ranges from Like New (Open Box) to
            Damaged. All units have been data-cleared by an R2-certified
            facility, however not all features have been tested. The vast
            majority of units have been returned by customer within less than 90
            days of original purchase. 30% of units in use for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Security Cameras - Data Cleared</TextBold>
          <StyledInnerParagraph>
            All products are member returns and sold AS IS with no return
            privileges. Cosmetic condition ranges from Like New (Open Box) to
            Damaged. All units have been data-cleared by an R2-certified
            facility, however, not all features have been tested. Items are not
            inspected to include all items included in the originally purchased
            unit. The vast majority of units have been returned by the customer
            within less than 90 days of original purchase. 30% of units in use
            for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
        <ListItem>
          <TextBold>Locked Device - Apple</TextBold>
          <StyledInnerParagraph>
            Note that this listing is for bid solely by R2-certified buyers, who
            certify safeguarding of any potential customer data which may remain
            on these hard drives. These are Apple (FMiP) Locked Devices. The
            majority of these locked units were not officially tested in any
            manner to confirm damage or any other potential reason the items
            would be defective. All products are member returns and sold AS IS
            with no return privileges. Cosmetic condition ranges from Like New
            (Open Box) to Damaged. The vast majority of units have been returned
            by customer within less than 90 days of original purchase. 40% of
            units in use for 9 days or less.
          </StyledInnerParagraph>
        </ListItem>
      </List>
      <SubHeader>Liquidation Lot Sizes</SubHeader>
      <StyledParagraph>
        Costco Wholesale Liquidation Auctions typically sell liquidated
        merchandise in a variety of lot sizes. Generally speaking, auctions are
        most often sold by the pallet. Typical lot sizes include one to ten
        pallet lots (also known as LTL or Less Than Truckload) and truckloads.
      </StyledParagraph>
      <StyledParagraph>Buying by the Pallet:</StyledParagraph>
      <List>
        <ListItem>
          Packaged inventory that can fit on an individual pallet, the smallest
          unit of retail storage efficiency. A pallet may also be packaged as
          oversized or double stacked.
        </ListItem>
        <ListItem>
          Typically a pallet is the classic wood construction but can also
          include plastic, metal or recycled material. A pallet may also rarely
          contain items that are packaged in boxes (such as for iPads or very
          high-end designer bags).
        </ListItem>
      </List>
      <StyledParagraph>Common Lot Sizes:</StyledParagraph>
      <List>
        <ListItem>
          LTL (Less Than Truckload)
          <SubList>
            <ListItem>
              A relatively small amount of liquidated freight which is defined
              as between 1&ndash;10 pallet spaces on a truck.
            </ListItem>
          </SubList>
        </ListItem>
        <ListItem>
          Full Truckloads
          <SubList>
            <ListItem>
              A truckload of liquidated inventory is typically defined as
              containing 11 or more pallet spaces of inventory.
            </ListItem>
            <ListItem>
              You can combine multiple LTL auctions to create a full truckload
              and save on shipping costs under certain circumstances. Please see
              the corresponding FAQ for more information.
            </ListItem>
          </SubList>
        </ListItem>
        <ListItem>
          Small Pack (Boxes):
          <SubList>
            <ListItem>
              A small amount of liquidated freight which is less than 1 pallet
              quantities, typically shipped in boxed and featuring free
              shipping.
            </ListItem>
          </SubList>
        </ListItem>
      </List>
      <SubHeader>
        Buy from Locations Near your Business to Save Time and Shipping Costs
      </SubHeader>

      <StyledParagraph>
        Individual auction listings highlight the location that the inventory in
        that listing will be shipped from. Auctions are closing daily, typically
        from the following locations:
      </StyledParagraph>

      <List>
        <ListItem>Atlanta, GA</ListItem>
        <ListItem>Carrollton, TX</ListItem>
        <ListItem>Dallas, TX</ListItem>
        <ListItem>Frederick, MD</ListItem>
        <ListItem>Fresno, CA</ListItem>
        <ListItem>Mira Loma, CA</ListItem>
        <ListItem>Monroe Township, NJ</ListItem>
        <ListItem>Morris, IL</ListItem>
        <ListItem>Salt Lake City, UT</ListItem>
        <ListItem>Sumner, WA</ListItem>
        <ListItem>Tolleson, AZ</ListItem>
        <ListItem>Tracy, CA</ListItem>
        <ListItem>Van Buren Township, MI</ListItem>
        <ListItem>West Palm Beach, FL</ListItem>
      </List>

      <SubHeader id="marketplace-buyer-requirements">
        Marketplace Buyer Requirements
      </SubHeader>
      <StyledParagraph>
        In order to view and bid on Costco auction listings, you must be an
        approved buyer. To successfully apply to the marketplace, you must
        provide A) a valid{' '}
        <StyledLink href="/faq/buyer-faq/applying-logging-in/how-do-i-get-a-resale-certificate/">
          state-specific resale certificate
        </StyledLink>{' '}
        for US buyers <TextBold>OR</TextBold> B) a Proof of Business for
        international buyers, when applying for marketplace approval. Please
        ensure that you provide a valid US address, as shipment must be accepted
        in the US.
      </StyledParagraph>
      <List>
        <ListItem>
          We recommend that you take a moment to review the list of
          state-specific resale forms by each state, as well as additional
          information on reseller certificates.
        </ListItem>
        <ListItem>
          Buyers must not resell inventory purchased from Costco Wholesale
          Liquidation Auctions within a 5 mile radius of a Costco retail store.
        </ListItem>
      </List>
      <SubHeader>Additional Resources</SubHeader>

      <StyledParagraph>
        If you have additional questions about Costco Wholesale Liquidations,
        please feel free to{' '}
        <StyledLink href="/contact-us/">
          <TextBold>Contact Us</TextBold>
        </StyledLink>
        !
      </StyledParagraph>
      <StyledParagraph>
        To join the Costco Wholesale Liquidations marketplace, please{' '}
        <StyledLink
          href="/acct/signup?siteAbb=cst"
          onClick={() => trackSignUpStart('costco_about')}
        >
          apply here
        </StyledLink>
        !
      </StyledParagraph>
    </Container>
  )
}

export default CostcoAboutDetails
